// src/oura/ui/Fold.tsx
//
// The card surface, and the card whose head folds it. Every panel on a
// redesigned screen folds by its head (YW, 25 Sep), and remembers it.

import { createContext, useContext, useId, useRef, useState, type ReactNode, type TransitionEvent } from "react";
import { ChevronRightIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { FOCUS_RING, SURFACE, TYPE } from "./tokens";
import { FoldScopeContext, foldKey, useFoldReveal, useFoldState } from "./foldState";
import { PanelIcon, type PanelIconSpec } from "./PanelIcon";
import { REDUCED_MOTION, useMediaQuery } from "./useMediaQuery";

/** Names the screen every FoldCard under it stores its state against. */
export function FoldScope({ value, children }: { value: string; children: ReactNode }) {
    return <FoldScopeContext.Provider value={value}>{children}</FoldScopeContext.Provider>;
}

/**
 * Every FoldCard under this renders as `alwaysOpen`: no chevron, no clickable
 * head. The merchant shell sets it, because an owner's screens hold too little
 * for folding to save anything, and a folded card only hid what they came for.
 * Staff screens leave it off and keep their folds.
 */
const NoFoldContext = createContext(false);

export function NoFold({ children }: { children: ReactNode }) {
    return <NoFoldContext.Provider value>{children}</NoFoldContext.Provider>;
}

/**
 * Whether this is the redesigned merchant shell, which is exactly where
 * NoFold is provided. Read by surfaces that look different there (a drawer's
 * white body). Decided by where the screen is drawn, not by the role: staff
 * viewing as the merchant (#4112) are staff, and see the merchant's screens.
 * Drawers portal out of the tree, and context follows them.
 */
export function useClientShell(): boolean {
    return useContext(NoFoldContext);
}

/** A card with no fold: padding 20, or 0 when `flush` (a table that runs edge to edge). */
export function Surface({ flush, className, children }: { flush?: boolean; className?: string; children: ReactNode }) {
    return <section className={cn(SURFACE.card, "flex flex-col", !flush && "p-5", className)}>{children}</section>;
}

export interface FoldCardProps {
    /** Stable id for the stored state. Never derived from the title (D14). */
    id: string;
    title: ReactNode;
    icon?: PanelIconSpec;
    /** Beside the chevron, visible open and folded. May hold a link. */
    right?: ReactNode;
    /** Shown only while folded, so a folded card still says what it holds. */
    foldedFact?: ReactNode;
    /** No body padding: for a table or list that runs to the card's edges. */
    flush?: boolean;
    /**
     * Start folded until someone opens it: for a screen's secondary tools
     * (a full table, an approval list). A stored choice always wins.
     */
    defaultFolded?: boolean;
    /**
     * This card does not fold. No chevron, no clickable head, no stored
     * state, and a card someone had folded before shows open.
     *
     * For a card that IS the screen rather than a tool on it: the trend, the
     * question-type grid, the rivals a scan found, Core Web Vitals. Folding
     * those only ever hid the answer somebody came for, and because the
     * choice was remembered they stayed hidden on the next visit with no clue
     * why the page looked empty (#4025 FE-13).
     */
    alwaysOpen?: true;
    /** DOM id on the section, for in-page anchors such as `#plan`. */
    anchor?: string;
    className?: string;
    children: ReactNode;
}

export function FoldCard({ id, title, icon, right, foldedFact, flush, defaultFolded, alwaysOpen: openProp, anchor, className, children }: FoldCardProps) {
    const noFold = useContext(NoFoldContext);
    const alwaysOpen = openProp || noFold;
    const key = foldKey(useContext(FoldScopeContext), id);
    const [stored, setFolded] = useFoldState(key, defaultFolded);
    // The hook still runs, so the stored value is left exactly as it is: this
    // prop can be taken off again without anyone losing a choice they made.
    const folded = alwaysOpen ? false : stored;
    const reducedMotion = useMediaQuery(REDUCED_MOTION);
    const headId = useId();
    const bodyId = useId();
    const sectionRef = useRef<HTMLElement>(null);
    const headRef = useRef<HTMLButtonElement>(null);
    /**
     * The body clips only while it is folded or moving.
     *
     * Clipping is what lets the rows collapse to nothing, but an open card
     * that clips would also cut off every menu and popover inside it. So the
     * clip goes on the moment a fold starts and comes off when an unfold
     * finishes; with reduced motion there is no transition to wait for.
     */
    const [clip, setClip] = useState(folded);

    const onToggle = () => {
        if (!folded) setClip(true);
        else if (reducedMotion) setClip(false);
        setFolded(!folded);
    };

    // Opened from a link elsewhere on the page (useFoldOpener): unfold in
    // place, then bring the card into view with focus on its head, so a
    // keyboard user lands where the page just scrolled to.
    useFoldReveal(key, () => {
        if (folded) onToggle();
        headRef.current?.focus({ preventScroll: true });
        sectionRef.current?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    });
    const onTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !folded) setClip(false);
    };

    return (
        <section
            ref={sectionRef}
            id={anchor}
            aria-labelledby={headId}
            // Folded, the card keeps its own height instead of stretching to
            // its bento row. `w-full` because in a flex column (BentoStack)
            // self-start is the horizontal axis, and the card would otherwise
            // shrink or grow to its title's width.
            className={cn(SURFACE.card, "flex flex-col", !flush && "p-5", folded && "w-full self-start", className)}
        >
            <div className={cn("transition-[margin] duration-300 motion-reduce:transition-none", flush ? "px-5 py-4" : !folded && "mb-3.5")}>
                {/* The title keeps at least 10rem and wraps rather than being
                    cut; past that, the right slot wraps to its own line (a tab
                    track on a phone) rather than squeezing the title to
                    nothing. The chevron is pinned to the first line so it
                    stays beside the title either way. */}
                <div className={cn("relative flex flex-wrap items-center gap-x-2.5 gap-y-2", !alwaysOpen && "pr-6")}>
                    <h2 className="m-0 min-w-0 flex-1 basis-40">
                        {alwaysOpen ? (
                            // Not a button: there is nothing to press. A
                            // control that does nothing is worse than none,
                            // for a pointer and for a screen reader both.
                            <span id={headId} className="flex w-full min-w-0 items-start gap-2.5 text-left text-gray-900">
                                {icon && <PanelIcon icon={icon.icon} area={icon.area} className="mt-0.5" />}
                                <span className={cn(TYPE.tt, "min-w-0 flex-1 whitespace-normal break-words")}>{title}</span>
                            </span>
                        ) : (
                            <button
                                ref={headRef}
                                type="button"
                                id={headId}
                                aria-expanded={!folded}
                                aria-controls={bodyId}
                                onClick={onToggle}
                                className={cn("flex w-full min-w-0 items-start gap-2.5 rounded-md text-left text-gray-900", FOCUS_RING)}
                            >
                                {/* Level with the first line of a title that wraps. */}
                                {icon && <PanelIcon icon={icon.icon} area={icon.area} className="mt-0.5" />}
                                <span className={cn(TYPE.tt, "min-w-0 flex-1 whitespace-normal break-words")}>{title}</span>
                            </button>
                        )}
                    </h2>
                    {((folded && foldedFact) || right) && (
                        <div className={cn(TYPE.tc, "ml-auto flex min-w-0 max-w-full shrink-0 items-center gap-2.5 text-gray-500")}>
                            {folded && foldedFact && <span className="shrink-0">{foldedFact}</span>}
                            {right}
                        </div>
                    )}
                    {/* A second target for the pointer only: the heading's
                        button is the control for keyboards and screen readers,
                        and a link in `right` cannot sit inside it. The glyph
                        stays 16px; the pseudo-element makes the target 40 by
                        48 for a thumb. */}
                    {!alwaysOpen && (
                        <button
                            type="button"
                            tabIndex={-1}
                            aria-hidden
                            onClick={onToggle}
                            // Centred on the head row, as the folded fact beside it is: at the top it sat above
                            // "3 answers" whenever the title ran to two lines (Jovi, 20 Oct).
                            className="absolute inset-y-0 right-0 my-auto grid h-6 w-4 shrink-0 place-items-center text-gray-400 after:absolute after:-inset-3 after:content-['']"
                        >
                            <ChevronRightIcon
                                size={14}
                                className={cn("transition-transform duration-200 motion-reduce:transition-none", !folded && "rotate-90")}
                            />
                        </button>
                    )}
                </div>
            </div>
            <div
                id={bodyId}
                inert={folded}
                onTransitionEnd={onTransitionEnd}
                className={cn(
                    // grid-cols-1 is minmax(0, 1fr): an auto column would grow to the
                    // widest unwrapped line inside, and a truncating row would
                    // push the card past a phone's width instead of truncating.
                    "grid grid-cols-1 transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
                    folded ? "grid-rows-[0fr]" : "flex-1 grid-rows-[1fr]",
                )}
            >
                {/* Positioned, so the clip also holds absolutely placed
                    descendants (a screen-reader span in a table): with no
                    positioned ancestor inside the card they escape it and
                    widen the page on a phone. */}
                <div className={cn("relative min-h-0 min-w-0", clip && "overflow-hidden")}>{children}</div>
            </div>
        </section>
    );
}
